// src/components/clinical/ClinicalRegionPicker.jsx
// Combined picker for body regions and functional areas during visit creation.
// Shows one or both sections based on template flags.

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  BODY_REGIONS, inferBodyRegionsFromICD10,
  FUNCTIONAL_AREAS, inferFunctionalAreasFromICD10,
} from '@prism-ehr/shared/constants';

export function ClinicalRegionPicker({
  isOpen,
  onConfirm,
  onCancel,
  icd10Codes = [],
  initialBodyRegions = [],
  initialFunctionalAreas = [],
  showBodyRegions = false,
  showFunctionalAreas = false,
  source,
}) {
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [inferredRegions, setInferredRegions] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [inferredAreas, setInferredAreas] = useState([]);

  useEffect(() => {
    if (showBodyRegions) {
      if (initialBodyRegions?.length > 0) {
        setSelectedRegions(initialBodyRegions);
        setInferredRegions(initialBodyRegions);
      } else if (icd10Codes?.length > 0) {
        const regions = inferBodyRegionsFromICD10(icd10Codes);
        setInferredRegions(regions);
        setSelectedRegions(regions);
      }
    }
  }, [initialBodyRegions, icd10Codes, showBodyRegions]);

  useEffect(() => {
    if (showFunctionalAreas) {
      if (initialFunctionalAreas?.length > 0) {
        setSelectedAreas(initialFunctionalAreas);
        setInferredAreas(initialFunctionalAreas);
      } else if (icd10Codes?.length > 0) {
        const areas = inferFunctionalAreasFromICD10(icd10Codes);
        setInferredAreas(areas);
        setSelectedAreas(areas);
      }
    }
  }, [initialFunctionalAreas, icd10Codes, showFunctionalAreas]);

  const toggleRegion = (id) => {
    setSelectedRegions(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleArea = (id) => {
    setSelectedAreas(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  if (!isOpen) return null;

  const totalSelected = selectedRegions.length + selectedAreas.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {showBodyRegions && showFunctionalAreas
              ? 'Select Regions & Functional Areas'
              : showBodyRegions
                ? 'Select Body Regions'
                : 'Select Functional Areas'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body Regions Section */}
        {showBodyRegions && (
          <div className={showFunctionalAreas ? 'mb-5' : ''}>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Body Regions</h4>
            <p className="text-xs text-gray-500 mb-3">
              Filter flow sheet items by anatomy.
            </p>

            {inferredRegions.length > 0 && (
              <p className="text-xs text-blue-600 mb-2">
                {source === 'case'
                  ? 'Pre-selected from case'
                  : source === 'encounter'
                    ? 'Pre-selected from encounter'
                    : 'Inferred from ICD-10 codes'}
              </p>
            )}

            <div className="grid grid-cols-3 gap-2">
              {BODY_REGIONS.map(region => {
                const isSelected = selectedRegions.includes(region.id);
                const isInferred = inferredRegions.includes(region.id);
                return (
                  <button
                    key={region.id}
                    type="button"
                    onClick={() => toggleRegion(region.id)}
                    className={`
                      px-3 py-2 rounded-lg border text-sm font-medium transition-colors
                      ${isSelected
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:text-primary'}
                      ${isInferred && isSelected ? 'ring-2 ring-primary/30' : ''}
                    `}
                  >
                    {region.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Divider */}
        {showBodyRegions && showFunctionalAreas && (
          <hr className="border-gray-200 mb-5" />
        )}

        {/* Functional Areas Section */}
        {showFunctionalAreas && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Functional Areas</h4>
            <p className="text-xs text-gray-500 mb-3">
              Drive conditional prefill and field visibility.
            </p>

            {inferredAreas.length > 0 && (
              <p className="text-xs text-blue-600 mb-2">
                {source === 'case'
                  ? 'Pre-selected from case'
                  : source === 'encounter'
                    ? 'Pre-selected from encounter'
                    : 'Inferred from ICD-10 codes'}
              </p>
            )}

            <div className="flex flex-col gap-2">
              {FUNCTIONAL_AREAS.map(area => {
                const isSelected = selectedAreas.includes(area.id);
                const isInferred = inferredAreas.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => toggleArea(area.id)}
                    className={`
                      px-4 py-3 rounded-lg border text-sm font-medium transition-colors text-left
                      ${isSelected
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:text-primary'}
                      ${isInferred && isSelected ? 'ring-2 ring-primary/30' : ''}
                    `}
                  >
                    <span>{area.label}</span>
                    {area.description && (
                      <span className={`block text-xs mt-0.5 ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                        {area.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onCancel}>
            Skip
          </Button>
          <Button onClick={() => onConfirm({ bodyRegions: selectedRegions, functionalAreas: selectedAreas })}>
            {totalSelected > 0
              ? `Apply (${totalSelected} selected)`
              : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
